/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { VideoProviderConfigModel } from "../../videoProvider";
import { PlaybackContentType } from "../enums";
import type { IMetadata, IPlayable } from "../interfaces";

/**
 * Decisions about a VOD playable that more than one part of the playback
 * stack has to make the same way.
 *
 * The controller decides from these whether to configure DRM and open a
 * session, and PlaybackURLManager decides from them which URL to hand the
 * player. If the two disagreed, a preview would wait on a session nobody
 * creates, or create one it never plays — so both read the answer here.
 */
export class VodPlaybackUtils {
  /**
   * The VOD provider partner id: the watch action's `partnerId`, which names
   * the provider actually serving the title, else the playable's
   * `videoProviderId`, else `""`.
   *
   * The watch action comes first because callers that start VOD from an
   * offer id know no provider up front; it is also the provider the session
   * is created against, so DRM must be configured for that same one.
   */
  public static getProviderId(
    playable: IPlayable,
    metadata: IMetadata
  ): string {
    const watchVodAction = metadata.watchVodAction;
    const kernel =
      watchVodAction !== undefined && watchVodAction !== null
        ? watchVodAction.kernel
        : undefined;

    if (
      kernel !== undefined &&
      kernel !== null &&
      typeof kernel.partnerId === "string" &&
      kernel.partnerId !== ""
    ) {
      return kernel.partnerId;
    }

    const vodModel = playable.vodPlaybackModel;

    if (vodModel !== undefined && vodModel !== null) {
      const vodId = vodModel.videoProviderId;
      if (vodId !== undefined && vodId !== null && vodId !== "") {
        return vodId;
      }
    }

    return "";
  }

  /**
   * Whether this is a VOD preview (trailer) the provider plays without a
   * streaming session — its `assetUrl` is then the only URL there is, and it
   * plays in the clear.
   *
   * Driven by the provider's `needSessionCreateForPreviews`; a provider that
   * does not declare it plays previews session-less, as in Kaveri.
   */
  public static isSessionlessPreview(
    playable: IPlayable,
    metadata: IMetadata
  ): boolean {
    if (playable.playbackContentType !== PlaybackContentType.VOD) {
      return false;
    }

    const vodModel = playable.vodPlaybackModel;

    if (
      vodModel === undefined ||
      vodModel === null ||
      vodModel.isPreview !== true
    ) {
      return false;
    }

    return !VideoProviderConfigModel.needsSessionCreateForPreviews(
      VodPlaybackUtils.getProviderId(playable, metadata)
    );
  }
}
